import { z } from 'zod';

export const AiProviderSchema = z.enum(['anthropic', 'openai']);
export type AiProvider = z.infer<typeof AiProviderSchema>;

/** Instellingen in `app_settings` (key/value JSONB). Deze schema beschrijft de samengestelde vorm. */
export const AppSettingsSchema = z.object({
  template_product_code: z.string().max(50).nullable().default(null),
  default_unit_code: z.string().max(10).default('STK'),
  ai_provider: AiProviderSchema.default('anthropic'),
  ai_model: z.string().max(100).nullable().default(null),
  lookup_concurrency: z.number().int().min(1).max(5).default(2),
  lookup_auto_enqueue: z.boolean().default(false),
  lookup_topk: z.number().int().min(1).max(20).default(5),
  push_use_blocked_commit: z.boolean().default(false),
});
export type AppSettings = z.infer<typeof AppSettingsSchema>;

/** Wat een gewone user mag zien. */
export const PublicSettingsKeys = ['template_product_code', 'default_unit_code', 'lookup_topk'] as const;

export const UpdateSettingsBodySchema = AppSettingsSchema.partial();
export type UpdateSettingsBody = z.infer<typeof UpdateSettingsBodySchema>;

export const AiStatusSchema = z.object({
  provider: AiProviderSchema,
  model: z.string().nullable(),
  providers: z.record(
    z.object({ configured: z.boolean(), defaultModel: z.string(), models: z.array(z.string()) }),
  ),
});
export type AiStatus = z.infer<typeof AiStatusSchema>;

export const Unit4StatusSchema = z.object({
  env: z.enum(['test', 'prod']),
  baseUrl: z.string(),
  adminCode: z.string(),
  authMode: z.enum(['apikey', 'basic']),
  reachable: z.boolean(),
  latencyMs: z.number().nullable(),
  error: z.string().nullable(),
});
export type Unit4Status = z.infer<typeof Unit4StatusSchema>;

/**
 * Klingerator-sjabloon: eigen naam (bv. afdeling) → Unit4-donorproduct (superSjabloon) dat de
 * administratieve velden levert, plus per-categorie overrides (categoriecode → groepscode) die
 * bij de push over de donorclassificatie heen gelegd worden.
 */
export const TemplateVariantSchema = z.enum(['m', 'tr']);

/**
 * Beoordeling van de technische classificatie van een sjabloon (BugHerd #1450); NULL = in orde.
 * no_products = geen Unit4-voorbeelden met deze PGK, few_examples = 1–2 voorbeelden,
 * undecided = een categorie (KTI/CBS/KLI) onder de 60 %.
 */
export const TemplateClassificationReviewSchema = z.enum(['no_products', 'few_examples', 'undecided']);
export type TemplateClassificationReview = z.infer<typeof TemplateClassificationReviewSchema>;

export const ProductTemplateSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  template_product_code: z.string(),
  category_overrides: z.record(z.string(), z.string()),
  /** Journaalpost (Unit4 referenceJournalEntryCode) voor nieuwe producten; NULL = die van de donor */
  journal_entry_code: z.string().nullable(),
  /** Afdeling waar dit sjabloon bij hoort (Appendages, Slangen, …) */
  department: z.string().nullable(),
  product_line: z.string().nullable(),
  product_group: z.string().nullable(),
  product_type: z.string().nullable(),
  /** m = Klinger-eigen maakwerk, tr = handel (NON-Klinger) */
  variant: TemplateVariantSchema.nullable(),
  /** Rijke omschrijving uit de spreadsheet — matchingsbasis voor de AI-sjabloonkeuze */
  match_description: z.string().nullable(),
  /** false = "niet in gebruik": bewaard, maar niet in keuzelijsten/AI */
  active: z.boolean(),
  notes: z.string().nullable(),
  /** Tag in de sjablonenlijst; NULL = in orde */
  classification_review: TemplateClassificationReviewSchema.nullable(),
  classification_review_note: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProductTemplate = z.infer<typeof ProductTemplateSchema>;

export const ProductTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  template_product_code: z.string().trim().min(1).max(50),
  category_overrides: z.record(z.string().trim().min(1).max(10), z.string().trim().min(1).max(20)).optional(),
  /** Weglaten = ongewijzigd laten (bv. bij de Excel-import); null = die van de donor */
  journal_entry_code: z.string().trim().max(10).optional().nullable(),
  department: z.string().trim().max(100).optional().nullable(),
  product_line: z.string().trim().max(100).optional().nullable(),
  product_group: z.string().trim().max(200).optional().nullable(),
  product_type: z.string().trim().max(200).optional().nullable(),
  variant: TemplateVariantSchema.optional().nullable(),
  match_description: z.string().trim().max(4000).optional().nullable(),
  active: z.boolean().optional(),
  notes: z.string().trim().max(1000).optional().nullable(),
  /** Weglaten = ongewijzigd laten (bv. bij de Excel-import); null = in orde */
  classification_review: TemplateClassificationReviewSchema.optional().nullable(),
  classification_review_note: z.string().trim().max(1000).optional().nullable(),
});
export type ProductTemplateBody = z.infer<typeof ProductTemplateBodySchema>;

/** Controle van een sjablooncode tegen Unit4 (verplichte productcategorieën, assemblage-eisen). */
export const TemplateCheckSchema = z.object({
  code: z.string(),
  exists: z.boolean(),
  isAssembly: z.boolean(),
  hasAssemblyJournal: z.boolean(),
  description: z.string().nullable(),
  requiredCategories: z.array(z.object({ code: z.string(), description: z.string() })),
  presentCategories: z.array(z.string()),
  /** Categorieën die via sjabloon-overrides (extra) gezet worden */
  overrideCategories: z.array(z.string()).optional().default([]),
  missingCategories: z.array(z.object({ code: z.string(), description: z.string() })),
  ok: z.boolean(),
  warnings: z.array(z.string()),
});
export type TemplateCheck = z.infer<typeof TemplateCheckSchema>;

/**
 * Wat een nieuw product van het sjabloon(donor)product overerft als het eigen veld leeg blijft —
 * live uit Unit4, voor weergave bij de override-velden op het tabblad Gegevens.
 */
export const TemplateInheritedSchema = z.object({
  code: z.string(),
  exists: z.boolean(),
  description: z.string().nullable(),
  productGroupCode: z.string().nullable(),
  journalEntryCode: z.string().nullable(),
  assemblyJournalEntryCode: z.string().nullable(),
  warehouseCode: z.string().nullable(),
  locationCode: z.string().nullable(),
  /** EAN wordt bewust níét overgenomen van het sjabloon (uniek per product) — alleen ter info */
  eanCode: z.string().nullable(),
  costPrice: z.string().nullable(),
  assemblyWarehouseCode: z.string().nullable(),
  usePartialRelocation: z.enum(['J', 'N']).nullable(),
  useComponentPartial: z.enum(['J', 'N']).nullable(),
  partialRelocationMinimum: z.string().nullable(),
  applySubAssemblyEndProduct: z.enum(['J', 'N']).nullable(),
  applySubAssemblyComponent: z.enum(['J', 'N']).nullable(),
  /** Classificatie van de donor per categorie (categorie → groepscode) */
  categories: z.record(z.string(), z.string()),
});
export type TemplateInherited = z.infer<typeof TemplateInheritedSchema>;

/** Productcategorieën met hun groepswaarden (keuzelijsten voor sjabloon-overrides). */
export const CategoryGroupsSchema = z.object({
  categories: z.array(
    z.object({
      code: z.string(),
      description: z.string(),
      mandatory: z.boolean(),
      groups: z.array(z.object({ code: z.string(), description: z.string() })),
    }),
  ),
});
export type CategoryGroups = z.infer<typeof CategoryGroupsSchema>;

/** Rapport van een sjabloon-import (Excel): per rij wat er zou gebeuren / gebeurd is. */
export const TemplateImportRowSchema = z.object({
  row: z.number().int(),
  pgk: z.string().nullable(),
  name: z.string(),
  action: z.enum(['create', 'update', 'unchanged', 'skip']),
  detail: z.string().nullable(),
});
export const TemplateImportReportSchema = z.object({
  applied: z.boolean(),
  format: z.enum(['origineel', 'klingerator']),
  department: z.string(),
  donorCode: z.string(),
  rows: z.array(TemplateImportRowSchema),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  skipped: z.number().int(),
  warnings: z.array(z.string()),
});
export type TemplateImportRow = z.infer<typeof TemplateImportRowSchema>;
export type TemplateImportReport = z.infer<typeof TemplateImportReportSchema>;
